import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy } from "lucide-react";
import { fetchAds } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const absUrl = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const getYoutubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : null;
};

const AdSlot = ({ placement, slugs, regions }) => {
  const [ads, setAds] = useState([]);
  const { t, lang } = useLang();
  const sponsoredLabel = t?.common?.sponsored || "Sponsored";
  const isNl = lang === "nl";

  useEffect(() => {
    fetchAds(placement, { slugs, regions }).then(setAds).catch(() => setAds([]));
  }, [placement, JSON.stringify(slugs || []), JSON.stringify(regions || [])]);

  if (ads.length === 0) return null;
  const ad = ads[0];
  const isAuctionWinner = !!ad.is_auction_winner;
  const winnerLabel = isAuctionWinner ? (isNl ? "Featured app van de week" : "Featured App of the Week") : sponsoredLabel;

  const click = () => {
    if (ad.target_url) window.open(ad.target_url, "_blank", "noopener");
  };

  // ---- Video ad -----------------------------------------------------------
  if (ad.type === "video" && ad.video_url) {
    const ytId = getYoutubeId(ad.video_url);

    // --- YouTube full-bleed cinematic background ad ---
    if (ytId) {
      const ytSrc = `https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&mute=1&loop=1&playlist=${ytId}&controls=0&showinfo=0&modestbranding=1&rel=0&playsinline=1&iv_load_policy=3&disablekb=1`;
      return (
        <section className="relative w-full overflow-hidden bg-black my-12" data-testid={`adslot-${placement}`}>
          {/* 21:9 cinematic ratio for true background feel */}
          <div onClick={click} className="cursor-pointer relative w-full" style={{ paddingTop: "42.85%" }}>
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {/* Scale iframe to hide YouTube chrome around the edges */}
              <iframe
                src={ytSrc}
                title={ad.title}
                allow="autoplay; encrypted-media; picture-in-picture"
                frameBorder="0"
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute top-1/2 left-1/2"
                style={{
                  width: "150%",
                  height: "150%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            </div>
            {/* Dark overlay for text legibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/30 pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 pointer-events-none">
              <div className="hac-container pb-8 md:pb-14 text-white">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/75 mb-2">{sponsoredLabel}</div>
                <h3 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight drop-shadow-lg max-w-3xl">{ad.title}</h3>
                {ad.subtitle && <p className="text-base md:text-lg text-white/90 mt-3 max-w-2xl drop-shadow-md">{ad.subtitle}</p>}
              </div>
            </div>
          </div>
        </section>
      );
    }

    // --- Direct video file (mp4, R2-hosted) ---
    return (
      <div className="hac-container my-10" data-testid={`adslot-${placement}`}>
        <div onClick={click} className="cursor-pointer relative overflow-hidden rounded-2xl border border-[#E2E8F0] group">
          <video src={absUrl(ad.video_url)} autoPlay loop muted playsInline className="w-full max-h-[420px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 text-white">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 mb-1">{sponsoredLabel}</div>
            <h3 className="text-2xl md:text-3xl font-bold">{ad.title}</h3>
            {ad.subtitle && <p className="text-sm text-white/85 mt-1 max-w-2xl">{ad.subtitle}</p>}
          </div>
        </div>
      </div>
    );
  }

  // ---- Square ad ----------------------------------------------------------
  if (ad.type === "square" && ad.image_url) {
    return (
      <div className="hac-container my-10" data-testid={`adslot-${placement}`}>
        <div onClick={click} className="cursor-pointer max-w-md mx-auto bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden hover:border-[#2563EB] transition-colors">
          <img src={absUrl(ad.image_url)} alt={ad.title} className="w-full aspect-square object-cover" />
          <div className="p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mb-1">{sponsoredLabel}</div>
            <h3 className="font-bold text-[#0F172A]">{ad.title}</h3>
            {ad.subtitle && <p className="text-sm text-[#64748B] mt-1">{ad.subtitle}</p>}
          </div>
        </div>
      </div>
    );
  }

  // ---- Banner: auction-winner variant ------------------------------------
  if (isAuctionWinner) {
    return (
      <div className="hac-container my-10" data-testid={`adslot-${placement}`}>
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-amber-50 via-white to-amber-50 border-2 border-amber-300 shadow-lg ring-1 ring-amber-200" data-testid="adslot-auction-winner">
          {/* Top ribbon */}
          <div className="bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 text-amber-50 px-5 py-2 flex items-center justify-between gap-3 flex-wrap text-xs">
            <div className="inline-flex items-center gap-2 font-bold uppercase tracking-widest">
              <Trophy size={14} className="fill-current" /> {winnerLabel}
            </div>
            <Link to="/auction" className="inline-flex items-center gap-1 underline-offset-2 hover:underline text-amber-50/95" data-testid="adslot-auction-link">
              {isNl ? "Hoe werkt de veiling?" : "How does the auction work?"} →
            </Link>
          </div>

          <div onClick={click} className="cursor-pointer relative grid md:grid-cols-[1fr_auto] items-center">
            {ad.image_url && (
              <img src={absUrl(ad.image_url)} alt={ad.title} className="w-full h-[180px] md:h-[260px] object-cover" />
            )}
            <div className="absolute inset-0 md:relative md:bg-gradient-to-l md:from-amber-50 md:via-amber-50/90 md:to-transparent" />
            <div className="absolute md:relative bottom-0 left-0 right-0 md:bottom-auto md:left-auto md:right-auto md:max-w-md md:px-8 md:py-7 p-5 md:p-7 bg-gradient-to-t from-black/90 via-black/60 to-transparent md:bg-none text-white md:text-[#0F172A]">
              <h3 className="text-xl md:text-2xl font-bold leading-tight">{ad.title}</h3>
              {ad.subtitle && <p className="text-sm mt-1.5 text-white/90 md:text-[#64748B] max-w-md">{ad.subtitle}</p>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Banner: standard ad -----------------------------------------------
  return (
    <div className="hac-container my-10" data-testid={`adslot-${placement}`}>
      <div onClick={click} className="cursor-pointer relative overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white hover:border-[#2563EB] transition-colors">
        {ad.image_url && (
          <img src={absUrl(ad.image_url)} alt={ad.title} className="w-full h-[180px] md:h-[220px] object-cover" />
        )}
        <div className="absolute top-3 left-3 bg-white/90 text-[#0F172A] text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full">
          {sponsoredLabel}
        </div>
        <div className="p-5 md:p-6">
          <h3 className="text-xl md:text-2xl font-bold text-[#0F172A]">{ad.title}</h3>
          {ad.subtitle && <p className="text-sm text-[#64748B] mt-1 max-w-2xl">{ad.subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

export default AdSlot;
